import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface MenuItem {
  text: string;
  path: string;
  icon: string;
  roles?: string[];
  badge?: string;
}

const Sidebar: React.FC<{ collapsed: boolean; onToggle: () => void; mobileOpen: boolean; onMobileClose: () => void }> = ({ 
  collapsed, 
  onToggle, 
  mobileOpen, 
  onMobileClose 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Get user role - handle both JWT payload and full user object
  const userRole = user?.roleName || user?.role?.name || null;
  
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;
  const isPartner = userRole === 'partner';

  // Define menu items with role restrictions
  const menuItems: MenuItem[] = [
    // Dashboard for all users
    { text: 'Dashboard', path: '/dashboard', icon: '🏠' },
    
    // Partner-specific dashboard
    { text: 'Partner Dashboard', path: '/partner-dashboard', icon: '🚛', roles: ['partner'] },
    
    // Admin-only items
    { text: 'Users', path: '/users', icon: '👤', roles: ['admin', 'super_admin'] },
    { text: 'Admin Management', path: '/admin', icon: '👑', roles: ['super_admin'], badge: 'Super Admin' },
    { text: 'Roles & Permissions', path: '/roles', icon: '🔐', roles: ['admin', 'super_admin'] },
    { text: 'Permissions', path: '/permissions', icon: '⚙️', roles: ['admin', 'super_admin'] },
    { text: 'Categories', path: '/categories', icon: '🗂️', roles: ['admin', 'super_admin'] },
    { text: 'Employees', path: '/employees', icon: '👥', roles: ['admin', 'super_admin'] },
    { text: 'Services', path: '/services', icon: '🧰', roles: ['admin', 'super_admin'] },
    { text: 'Orders', path: '/orders', icon: '🧾', roles: ['admin', 'super_admin'] },
    { text: 'Offers', path: '/offers', icon: '🏷️', roles: ['admin', 'super_admin'] },
    { text: 'Pages', path: '/pages', icon: '📄', roles: ['admin', 'super_admin'] },
  ];

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(userRole);
  });

  const handleNavigation = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const content = (
    <aside className={`h-full border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 transition-all flex flex-col ${collapsed ? 'w-16' : 'w-60'}`}>
      {/* Header - Fixed */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 h-14 border-b border-gray-200 dark:border-gray-800">
        <button
          onClick={onToggle}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
          aria-label="Toggle sidebar"
          title="Toggle sidebar"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700 dark:text-gray-200">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Menu</span>
            {isSuperAdmin && (
              <span className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-full">
                Super Admin
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1">
        {filteredMenuItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <button
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors relative ${
                active 
                  ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-r-2 border-blue-600' 
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              title={collapsed ? item.text : undefined}
            >
              <span className="text-lg">{item.icon}</span>
              {!collapsed && (
                <div className="flex-1 flex items-center justify-between">
                  <span className="truncate">{item.text}</span>
                  {item.badge && (
                    <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </nav>
      
      {/* User Info at bottom - Fixed */}
      {!collapsed && (
        <div className="flex-shrink-0 p-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
              {user?.firstName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {user?.firstName || user?.email?.split('@')[0] || 'User'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {userRole === 'super_admin' ? 'Super Admin' : 
                 userRole === 'admin' ? 'Admin' : 
                 userRole === 'partner' ? 'Partner' : 'User'}
              </div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );

  return (
    <>
      {/* Desktop */}
      <div className="hidden md:block sticky top-0 h-screen">{content}</div>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={onMobileClose} />
          <div className="absolute left-0 top-0 h-full z-50 shadow-xl">{content}</div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
